import { Link } from 'react-router-dom';

import { DataTable, Paginator, Chip, FilterBar } from 'components';
import { routes, ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import columns from './columns';
import { statusLabel, statusColor, STATUS_OPTIONS } from './statuses';
import { CargoClaim, CargoClaimsFilter } from './CargoClaims.types';

import './cargo-claims.scss';

interface CargoClaimsUiProps {
  claims: CargoClaim[];
  totalRecords: number;
  loading?: boolean;
  filter: CargoClaimsFilter;
  handlers: {
    handleSearchChange: (name: string, value: string) => void;
    handleSubmitSearch: () => void;
    handleDropdownChange: (name: string, value: string) => void;
    handlePageChange: (event: any) => void;
  };
}

function amountBody(row: CargoClaim) {
  return `${row.amount.toLocaleString()} ${row.currency}`;
}

function creditNoteBody(row: CargoClaim) {
  if (!row.creditNote) return 'N/A';

  return `${row.creditNote.invoiceNumber}, ${row.creditNote.amount.toLocaleString()} ${row.creditNote.currency}`;
}

function statusBody() {
  return function (row: CargoClaim) {
    return (
      <Chip
        label={statusLabel(row.status)}
        color={statusColor(row.status)}
      />
    );
  };
}

function referenceBody(row: CargoClaim) {
  return (
    <Link
      className="cargo-claims__reference"
      to={`${routes.cargoClaims.index}/${row.reference}`}
    >
      {row.reference}
    </Link>
  );
}

export default function CargoClaimsUi({
  claims,
  totalRecords,
  loading,
  filter,
  handlers,
}: CargoClaimsUiProps) {
  const bodies: Record<string, (row: CargoClaim) => any> = {
    reference: referenceBody,
    amount: amountBody,
    status: statusBody(),
    creditNote: creditNoteBody,
  };

  const tableColumns = columns.map((column) => ({
    ...column,
    body: bodies[column.field],
  }));

  return (
    <div className="cargo-claims">
      <FilterBar
        loading={loading}
        search={{
          inputs: [
            {
              name: 'shipmentNumber',
              placeholder: 'Shipment #',
              value: filter.shipmentNumber,
            },
            {
              name: 'hawbNumber',
              placeholder: 'HAWB #',
              value: filter.hawbNumber,
            },
            /*
             * Typed rather than chosen. A dropdown could only offer the brands
             * on the page in front of you now that the service pages the rows,
             * which would silently hide every brand not on it. The service
             * matches a brand on its prefix, so typing part of one works.
             */
            {
              name: 'brand',
              placeholder: 'Brand',
              value: filter.brand,
            },
          ],
          onChange: handlers.handleSearchChange,
          onSubmit: handlers.handleSubmitSearch,
          width: 9,
        }}
        filters={[
          {
            kind: 'dropdown',
            name: 'status',
            placeholder: 'Status',
            value: filter.status,
            options: STATUS_OPTIONS,
            onChange: (event: any) =>
              handlers.handleDropdownChange('status', event.value),
            width: 3,
          },
        ]}
      />

      <DataTable
        value={claims}
        columns={tableColumns}
        dataKey="reference"
        totalRecords={totalRecords}
        emptyMessage="No claims match these filters. Clear them to see all claims."
        stripedRows
      />

      {totalRecords > 0 && (
        <Paginator
          first={filter.first}
          rows={filter.rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={handlers.handlePageChange}
        />
      )}
    </div>
  );
}
